import { OverviewWebComponent } from '..';
import { OverviewView } from '../view/OverviewView';
import { templateOverview } from '../view/template';
import { BaseWidgetController } from '../../widget/BaseWidgetController';
import { GetOverviewInformationResponse } from '@nirvana/shared/dtos/types/widgets/common';
import { IListenerConfig } from '../../framework/BaseWebComponentController';
import { SendDownloadLinksDialog } from '../../packages/uiElements/dialog/sendDownloadLinksDialog';
import {
  GetDefaultEndpointPackageAndLinksRequest,
  GetDefaultEndpointPackageAndLinksResponse,
  GetDownloadLinkRequest,
  GetDownloadLinkResponse,
  OsTypes,
  OverviewEvents,
} from '../types/OverviewTypes';
import { GzToastNotificationWebComponent } from '../../ui/gzToastNotification';
import { ToastSeverity } from '../../ui/gzToastNotification/type';
import { translateFn } from '../../utils/translator.helper';
import { ConsoleRequest, DataProviderType } from '../../utils/request';

export class OverviewController extends BaseWidgetController<GetOverviewInformationResponse> {
  public component: OverviewWebComponent;
  public view: OverviewView;
  public viewConstructor: typeof OverviewView;

  constructor(component: OverviewWebComponent) {
    super(component);
    this.initialWidgetSetup({
      viewConstructor: OverviewView,
      viewTemplate: templateOverview,
      dataFetchMethod: this.component.dependencies.api.clients.Widgets.getOverviewInformation,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$managedEndpointsContainer,
        event: OverviewEvents.INSTALL_NOW,
        callback: this.handleInstallNowButtonClicked,
      },
      {
        element: this.view.dom.$managedEndpointsContainer,
        event: OverviewEvents.SEND_DOWNLOAD_LINKS,
        callback: this.handleSendDownloadLinksButtonClicked,
      },
    ];
  }

  public async handleInstallNowButtonClicked() {
    const platform = this.detectOs();

    if (!platform) {
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: translateFn('Installation unavailable, the operating system is not supported'),
        severity: ToastSeverity.ERROR,
      });
      return;
    }

    try {
      const result = await this.getDownloadLink({ platform });

      const iframe = document.createElement('iframe');
      iframe.src = result.downloadLink;
      iframe.style.display = 'none';

      document.body.appendChild(iframe);
    } catch (e) {
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: translateFn('Failed to download installation package'),
        severity: ToastSeverity.ERROR,
      });
    }
  }

  public async handleSendDownloadLinksButtonClicked() {
    const companyId = this.component.targets[0].companyId;
    const wrapper = document
      .querySelector('gz-generic-dashboard-app')
      ?.shadowRoot?.querySelector('gz-dashboard-macro-component')
      ?.shadowRoot?.getElementById('fullscreen-content') as HTMLElement;

    if (!wrapper) {
      return;
    }
    let result;

    try {
      result = await this.getDefaultEndpointPackageAndLinks({ companyId });
    } catch (error) {
      throw new Error('Could not get default packages');
    }

    const dialogsDependencies = {
      ...this.dependencies,
      api: {
        ...this.dependencies.api,
        type: 'gz',
      },
      isPartner: globalThis.Application.Main.company.isPartner,
    };

    await SendDownloadLinksDialog.open(dialogsDependencies, wrapper, result.package, result.links);
  }

  async getDownloadLink(params: GetDownloadLinkRequest): Promise<GetDownloadLinkResponse> {
    const response = await ConsoleRequest.createRequest<string>({
      dataProviderType: DataProviderType.CORE,
      endpoint: 'EssentialSteps',
      method: 'getPackageLink',
      payload: {
        platform: params.platform,
      },
    });

    return { downloadLink: response };
  }

  async getDefaultEndpointPackageAndLinks(
    params: GetDefaultEndpointPackageAndLinksRequest
  ): Promise<GetDefaultEndpointPackageAndLinksResponse> {
    return await ConsoleRequest.createRequest<GetDefaultEndpointPackageAndLinksResponse>({
      dataProviderType: DataProviderType.CORE,
      endpoint: 'Packages',
      method: 'getDefaultPackageForEndpointAndItsDownloadLinks',
      payload: {
        companyId: params.companyId,
      },
    });
  }

  private detectOs(): OsTypes | undefined {
    const userAgent = navigator.userAgent;

    if (userAgent.indexOf('Win') !== -1) {
      return OsTypes.WINDOWS;
    } else if (userAgent.indexOf('Mac') !== -1) {
      return OsTypes.MAC_OS;
    } else if (userAgent.indexOf('Linux') !== -1) {
      return OsTypes.LINUX;
    }

    return undefined;
  }
}
